import React, { FC, memo, useCallback, useContext, useMemo, useState } from 'react'

import styles from './TagsSelect.module.scss'
import { TagsSelectContext, TagsSelectContextProvider } from './TagsSelectContext'
import TagsSubmenu from './TagsSubmenu'

import Select, { SelectProps } from '~elements/SelectV2'
import { TagFragment } from '~generated/operations'
import { TagTypes } from '~generated/types'
import DotIcon from '~icons/dot.svg'
import PlusSmallIcon from '~icons/plus-small.svg'
import { calculateLabelColor } from '~lib/helpers'
import Dots from '~lib/ui-kit/Dots'
import Option from '~lib/ui-kit/Option'
import TagsGroup from '~lib/ui-kit/TagsGroup'
import { TagProps } from '~lib/ui-kit/tags/TagOld'
import { tagsSelectGroupKey } from '~modules/tags/containers/TagsSelect/utils'
import { CreateTagData } from '~modules/tags/usecases/useCreateTag'
import tagNamesVariants from '~modules/tags/utils'
import Flex from '~shared/ui/Flex'
import Text from '~shared/ui/Text'

export type TagsSelectProps = Omit<
  SelectProps<string, string[]>,
  'multiple' | 'searchable' | 'creatable' | 'onCreate' | 'children' | 'size'
> & {
  type?: TagTypes
  tags?: TagFragment[]
  createable?: boolean
  onCreate?: (data: CreateTagData) => Promise<TagFragment>
  displayTags?: boolean
  wrap?: boolean
  tagsVariant?: TagProps['variant']
  size?: TagProps['size']
  readOnlyTags?: TagFragment[]
  displayLimit?: number
  hidePlusIcon?: boolean
  compact?: boolean
  tagGroupClassName?: string
  groupOrder?: string[]
  menuWidth?: number
}

// Can't switch this

const TagsSelect: FC<React.PropsWithChildren<TagsSelectProps>> = memo(
  ({
    type,
    bordered = true,
    tags,
    createable = true,
    interactive = true,
    displayTags,
    hidePlusIcon = false,
    disabled,
    readOnly,
    value,
    variant,
    wrap,
    onChange,
    onCreate,
    tagsVariant = 'dark',
    size,
    readOnlyTags = [],
    displayLimit,
    compact,
    tagGroupClassName,
    groupOrder,
    menuWidth,
    fitContent,
    listboxPlacement,
    label,
    ...elemProps
  }) => {
    const { tags: tagsFromContext, onCreate: onCreateFromContext } = useContext(TagsSelectContext)

    const tagsToUse = useMemo(() => {
      const arr = [...readOnlyTags, ...(tags || tagsFromContext || [])]
      if (!value) return arr
      // TODO Fix blockers in Select component and add ability to display selected items at the top of the list
      return [...arr.filter((tag) => value.includes(tag.id)), ...arr.filter((tag) => !value.includes(tag.id))]
    }, [readOnlyTags, tags, tagsFromContext, value])

    const onCreateHandler = onCreate || onCreateFromContext

    const [createLoading, setCreateLoading] = useState(false)

    function renderTagsIcon(val: string[]) {
      if (!value || value.length === 0) {
        return <DotIcon />
      }

      const colors = tagsToUse
        ?.filter((tag) => val?.includes(tag.id))
        .slice(0, 3)
        .map((item) => item.color)

      return <Dots colors={colors} />
    }

    function renderTagsValue(val?: string[]) {
      const tagsToDisplay = displayLimit
        ? tagsToUse.filter((tag) => val?.includes(tag.id)).slice(-displayLimit)
        : tagsToUse.filter((tag) => val?.includes(tag.id))

      if (tagsToDisplay.length > 0) {
        return (
          <Flex align="center" justify="center" gap="0-5">
            {!hidePlusIcon && !disabled && !readOnly && (
              <div className={styles.plus}>
                <PlusSmallIcon />
              </div>
            )}

            <TagsGroup
              className={tagGroupClassName}
              tags={tagsToDisplay}
              bordered={bordered}
              compact={compact}
              variant={tagsVariant}
              size={size}
            />
          </Flex>
        )
      }

      if (disabled || readOnly) {
        return (
          <Text variant="secondary" type="caption" textType="body-sm" className={styles.text}>
            No tags
          </Text>
        )
      }

      return (
        <Flex align="center" justify="center" gap="0-5" {...elemProps}>
          {!hidePlusIcon && (
            <div className={styles.plus}>
              <PlusSmallIcon />
            </div>
          )}

          <div className={styles.text}>{label || 'Tags'}</div>
        </Flex>
      )
    }

    async function handleCreateTag(label: string) {
      try {
        setCreateLoading(true)

        const tag = await onCreateHandler?.({
          label,
          type,
          color: calculateLabelColor(),
        })

        return tag?.id
      } finally {
        setCreateLoading(false)
      }
    }

    /**
     * Don't send readOnly tags' ids to onChange callback
     * Because they don't belong to that entity
     * We can't add/remove them like normal tags
     */
    const handleChange = useCallback(
      (value: string[]) => {
        const readOnlyTagsIds = new Set(readOnlyTags.map((tag) => tag.id))

        const filteredValue = value.filter((v) => !readOnlyTagsIds.has(v))

        onChange?.(filteredValue)
      },
      [onChange, readOnlyTags]
    )

    const popoverPlacement = useMemo(() => {
      if (listboxPlacement !== undefined) return listboxPlacement
      return displayTags ? 'bottom-end' : 'bottom-start'
    }, [displayTags, listboxPlacement])

    return (
      <Select
        disabled={disabled}
        readOnly={readOnly}
        creatable={createable}
        loading={createLoading}
        multiple
        renderIcon={displayTags ? false : renderTagsIcon}
        variant={displayTags ? 'plain' : variant}
        searchable
        value={value}
        onChange={handleChange}
        onCreate={handleCreateTag}
        renderValue={displayTags ? renderTagsValue : undefined}
        listboxPlacement={popoverPlacement}
        inputPlaceholder={createable ? 'Add a tag …' : 'Search tags …'}
        bordered={bordered}
        interactive={interactive}
        fitContent={fitContent}
        groupKey={tagsSelectGroupKey}
        groupOrder={groupOrder}
        menuWidth={menuWidth}
        label={label}
        {...elemProps}
      >
        {tagsToUse?.map((tag) => (
          <Option
            key={tag.id}
            icon={<DotIcon fill={tag.color} />}
            type={!readOnlyTags?.map((tag) => tag.id).includes(tag.id) ? 'checkbox' : 'button'}
            value={tag.id}
            data-value={tagNamesVariants[tag.type] || tag.type}
          >
            {tag.label}
          </Option>
        ))}
      </Select>
    )
  }
)

/**
 * Component can be used on public and private routes an therefore users can be accessed via private/public api.
 * Because component can be nested deeply in the components three it is better to pass tags via context, instead of props drilling.
 */

export default Object.assign(TagsSelect, { Submenu: TagsSubmenu, Provider: TagsSelectContextProvider })
